// adminportal/src/pages/CompanyManagement.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Trash2, Edit, Users, AlertTriangle, Server } from 'lucide-react';
import { apiUrl } from '../config';
import SftpConnectorManager from '../components/SftpConnectorManager';
import SharedCreateCompanyModal from '../components/SharedCreateCompanyModal';
import EditCompanyModal from '../components/EditCompanyModal';
import Copyright from '../components/Copyright';

interface Company {
  companyId: string;
  companyName: string;
  companyUrl: string;
  companyState: string;
  isPrimary?: boolean;
  primaryContact?: string;
  primaryPhone?: string;
  primaryEmail?: string;
  address?: string;
  allowedIpAddresses?: string;
  allowAllIps?: boolean;
  userCount?: number;
  serviceAccount?: {
    userId: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    createdAt: string;
  };
}

export default function CompanyManagement() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    company: Company | null;
    removeFolders: boolean;
  }>({ show: false, company: null, removeFolders: false });

  const [createModal, setCreateModal] = useState<{
    show: boolean;
    formData: {
      companyName: string;
      companyUrl: string;
      companyState: string;
      primaryContact: string;
      primaryPhone: string;
      primaryEmail: string;
      createServiceAccount: boolean;
      serviceAccountPasswordOption: 'generated' | 'custom' | 'ssh-key';
      serviceAccountPassword: string;
    };
  }>({
    show: false,
    formData: {
      companyName: '',
      companyUrl: '',
      companyState: '',
      primaryContact: '',
      primaryPhone: '',
      primaryEmail: '',
      createServiceAccount: false,
      serviceAccountPasswordOption: 'generated',
      serviceAccountPassword: '',
    },
  });

  const [editModal, setEditModal] = useState<{
    show: boolean;
    company: Company | null;
  }>({
    show: false,
    company: null,
  });

  const [activeTab, setActiveTab] = useState<'companies' | 'connectors'>('companies');
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Load companies on component mount
  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setLoading(true);
    try {
      // Loading companies from API
      const res = await fetch(apiUrl('companies'));
      // Response status received
      // Response headers received

      if (!res.ok) {
        const errorText = await res.text();
        // Response error occurred
        throw new Error(errorText);
      }

      const data = await res.json();
      // Extract companies array from the response structure
      const companies = data.companies || data || [];
      setCompanies(companies);
      setMessage(`✅ Loaded ${companies.length} companies`);
    } catch (err: unknown) {
      // Error loading companies
      setMessage(
        `❌ Failed to load companies: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
      setCompanies([]); // Ensure companies is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCompany = async (company: Company, removeFolders: boolean) => {
    if (!company) return;

    setLoading(true);
    setMessage('');

    try {
      // First check if company has users
      if (company.userCount && company.userCount > 0) {
        setMessage(
          `❌ Cannot delete company "${company.companyName}" - it still has ${company.userCount} user(s). Please delete all users first.`
        );
        setDeleteModal({ show: false, company: null, removeFolders: false });
        return;
      }

      // Delete company
      const res = await fetch(`${apiUrl('companies')}/${encodeURIComponent(company.companyId)}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ removeFolders }),
      });

      if (!res.ok) throw new Error(await res.text());

      setMessage(
        `✅ Company "${company.companyName}" deleted successfully${removeFolders ? ' with folders removed' : ''}`
      );
      setDeleteModal({ show: false, company: null, removeFolders: false });

      // Reload companies
      await loadCompanies();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to delete company: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (company: Company) => {
    setDeleteModal({ show: true, company, removeFolders: false });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, company: null, removeFolders: false });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleCreateCompany = async () => {
    setLoading(true);
    setMessage('');

    try {
      // First create the company
      const companyData = {
        companyName: createModal.formData.companyName,
        companyUrl: createModal.formData.companyUrl,
        companyState: createModal.formData.companyState,
        primaryContact: createModal.formData.primaryContact,
        primaryPhone: createModal.formData.primaryPhone,
        primaryEmail: createModal.formData.primaryEmail,
      };

      const res = await fetch(apiUrl('companies'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(companyData),
      });

      if (!res.ok) throw new Error(await res.text());

      const companyResult = await res.json();
      let serviceAccountResult = null;

      // Create service account if requested
      if (createModal.formData.createServiceAccount && createModal.formData.primaryEmail) {
        try {
          // Generate service account username with SVC_ format
          const cleanCompanyName = createModal.formData.companyName
            .replace(/[^a-zA-Z0-9]/g, '')
            .toLowerCase();
          const randomDigits = Math.floor(Math.random() * 1000)
            .toString()
            .padStart(3, '0');
          const serviceAccountUsername = `SVC_${cleanCompanyName}${randomDigits}`;

          const serviceAccountData = {
            companyName: createModal.formData.companyName.trim(),
            companyEmail: createModal.formData.primaryEmail.trim(),
            companyId: companyResult.company?.companyId || companyResult.companyId,
            username: serviceAccountUsername,
            customPassword:
              createModal.formData.serviceAccountPasswordOption === 'custom'
                ? createModal.formData.serviceAccountPassword
                : undefined,
            sshPublicKey:
              createModal.formData.serviceAccountPasswordOption === 'ssh-key'
                ? createModal.formData.serviceAccountPassword
                : undefined,
            sendWelcomeEmail: false,
          };

          const serviceRes = await fetch(apiUrl('create-user'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(serviceAccountData),
          });

          if (serviceRes.ok) {
            serviceAccountResult = await serviceRes.json();
            // Service account created
          } else {
            // Service account creation failed
          }
        } catch {
          // Service account creation failed (non-critical)
        }
      }

      let successMessage = `✅ Company "${createModal.formData.companyName}" created successfully`;
      if (serviceAccountResult) {
        successMessage += ` with service account "${serviceAccountResult.serviceAccount?.username}"`;
      }

      setMessage(successMessage);
      setCreateModal({
        show: false,
        formData: {
          companyName: '',
          companyUrl: '',
          companyState: '',
          primaryContact: '',
          primaryPhone: '',
          primaryEmail: '',
          createServiceAccount: false,
          serviceAccountPasswordOption: 'generated',
          serviceAccountPassword: '',
        },
      });

      // Reload companies
      await loadCompanies();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to create company: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _closeCreateModal = () => {
    setCreateModal({
      show: false,
      formData: {
        companyName: '',
        companyUrl: '',
        companyState: '',
        primaryContact: '',
        primaryPhone: '',
        primaryEmail: '',
        createServiceAccount: false,
        serviceAccountPasswordOption: 'generated',
        serviceAccountPassword: '',
      },
    });
  };

  const openEditModal = (company: Company) => {
    setEditModal({
      show: true,
      company,
    });
  };

  const closeEditModal = () => {
    setEditModal({
      show: false,
      company: null,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleServiceAccountPasswordReset = async (username: string) => {
    // TODO: Replace with proper confirmation dialog
    // if (!confirm('Are you sure you want to reset the password for this service account?')) {
    //   return;
    // }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('reset-service-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      if (!res.ok) throw new Error(await res.text());

      const result = await res.json();
      setMessage(
        `✅ Password reset successfully for ${username}. New password: ${result.newPassword}`
      );

      // Reload companies to get updated data
      await loadCompanies();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to reset password: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleServiceAccountDelete = async (username: string) => {
    // TODO: Replace with proper confirmation dialog
    // if (
    //   !confirm(
    //     `Are you sure you want to delete the service account "${username}"? This action cannot be undone.`
    //   )
    // ) {
    //   return;
    // }

    setLoading(true);
    try {
      const res = await fetch(apiUrl('delete-user'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username }),
      });

      if (!res.ok) throw new Error(await res.text());

      setMessage(`✅ Service account "${username}" deleted successfully`);

      // Reload companies to get updated data
      await loadCompanies();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to delete service account: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEditSuccess = (message: string) => {
    setMessage(message);
  };

  const handleEditError = (message: string) => {
    setMessage(message);
  };

  return (
    <div className='p-4 max-w-6xl mx-auto'>
      <div className='flex items-center justify-between mb-6'>
        <h1 className='text-2xl font-semibold text-slate-100 flex items-center gap-2'>
          <Building2 className='w-6 h-6' />
          Company Management
        </h1>
        <div className='flex items-center gap-3'>
          <button
            onClick={() =>
              setCreateModal({
                show: true,
                formData: {
                  companyName: '',
                  companyUrl: '',
                  companyState: '',
                  primaryContact: '',
                  primaryPhone: '',
                  primaryEmail: '',
                  createServiceAccount: false,
                  serviceAccountPasswordOption: 'generated',
                  serviceAccountPassword: '',
                },
              })
            }
            className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2'
          >
            <Building2 className='w-4 h-4' />
            Create Company
          </button>
          <Link to='/admin-dashboard' className='text-blue-400 hover:text-blue-300 text-sm'>
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg border ${
            message.includes('❌')
              ? 'bg-red-900/20 border-red-700/50 text-red-300'
              : 'bg-green-900/20 border-green-700/50 text-green-300'
          }`}
        >
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className='flex border-b border-white/10 mb-6'>
        <button
          onClick={() => setActiveTab('companies')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'companies'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-slate-300'
          }`}
        >
          <Building2 className='w-4 h-4 inline mr-2' />
          Companies
        </button>
        <button
          onClick={() => setActiveTab('connectors')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'connectors'
              ? 'text-blue-400 border-b-2 border-blue-400'
              : 'text-slate-400 hover:text-slate-300'
          }`}
        >
          <Server className='w-4 h-4 inline mr-2' />
          SFTP Connectors
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'companies' && (
        <div className='grid gap-4'>
          {(companies || []).map(company => (
            <div
              key={company.companyId}
              className='p-4 border border-white/10 rounded-lg company-card'
            >
              <div className='flex items-center justify-between'>
                <div className='flex-1'>
                  <div className='flex items-center gap-3 mb-2'>
                    <h3 className='text-lg font-medium text-slate-100'>{company.companyName}</h3>
                    {company.isPrimary && (
                      <span className='px-2 py-1 text-xs bg-blue-600 text-white rounded-full'>
                        Primary
                      </span>
                    )}
                    <span className='flex items-center gap-1 text-sm text-slate-400'>
                      <Users className='w-4 h-4' />
                      {company.userCount || 0} users
                    </span>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300'>
                    <div>
                      <span className='text-slate-400'>State:</span> {company.companyState}
                    </div>
                    <div>
                      <span className='text-slate-400'>URL:</span> {company.companyUrl}
                    </div>
                    {company.primaryContact && (
                      <div>
                        <span className='text-slate-400'>Contact:</span> {company.primaryContact}
                      </div>
                    )}
                    {company.primaryEmail && (
                      <div>
                        <span className='text-slate-400'>Email:</span> {company.primaryEmail}
                      </div>
                    )}
                  </div>

                  {/* Service Account Display */}
                  {company.serviceAccount && (
                    <div className='mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg'>
                      <h4 className='text-sm font-medium text-blue-400 mb-2 flex items-center gap-2'>
                        <Server className='w-4 h-4' />
                        Service Account
                      </h4>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-2 text-sm'>
                        <div>
                          <span className='text-slate-400'>Username:</span>
                          <span className='text-slate-200 ml-2 font-mono'>
                            {company.serviceAccount.username}
                          </span>
                        </div>
                        <div>
                          <span className='text-slate-400'>Email:</span>
                          <span className='text-slate-200 ml-2'>
                            {company.serviceAccount.email}
                          </span>
                        </div>
                        <div>
                          <span className='text-slate-400'>Role:</span>
                          <span className='text-slate-200 ml-2'>{company.serviceAccount.role}</span>
                        </div>
                        <div>
                          <span className='text-slate-400'>Created:</span>
                          <span className='text-slate-200 ml-2'>
                            {new Date(company.serviceAccount.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className='flex items-center gap-2 ml-4'>
                  <button
                    onClick={() => {
                      setSelectedCompany(company);
                      setActiveTab('connectors');
                    }}
                    className='p-2 text-emerald-400 hover:text-emerald-300 hover:bg-white/5 rounded transition-colors'
                    title='Manage SFTP Connectors'
                  >
                    <Server className='w-4 h-4' />
                  </button>

                  <button
                    onClick={() => openEditModal(company)}
                    className='p-2 text-blue-400 hover:text-blue-300 hover:bg-white/5 rounded transition-colors'
                    title='Edit Company'
                  >
                    <Edit className='w-4 h-4' />
                  </button>

                  <button
                    onClick={() => openDeleteModal(company)}
                    disabled={company.isPrimary}
                    className={`p-2 rounded transition-colors ${
                      company.isPrimary
                        ? 'text-slate-500 cursor-not-allowed'
                        : 'text-red-400 hover:text-red-300 hover:bg-white/5'
                    }`}
                    title={company.isPrimary ? 'Cannot delete primary company' : 'Delete Company'}
                  >
                    <Trash2 className='w-4 h-4' />
                  </button>
                </div>
              </div>

              {company.isPrimary && (
                <div className='mt-3 p-2 bg-amber-900/20 border border-amber-700/50 rounded text-amber-300 text-sm flex items-center gap-2'>
                  <AlertTriangle className='w-4 h-4' />
                  Primary company cannot be deleted
                </div>
              )}
            </div>
          ))}

          {loading && <div className='text-center py-8 text-slate-400'>Loading companies...</div>}

          {!loading && companies.length === 0 && (
            <div className='text-center py-8 text-slate-400'>No companies found</div>
          )}
        </div>
      )}

      {activeTab === 'connectors' && (
        <div>
          {selectedCompany ? (
            <div>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-semibold text-slate-100'>
                  {selectedCompany.companyName} Connectors
                </h2>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className='px-3 py-1 text-sm bg-slate-600 text-slate-200 rounded hover:bg-slate-500 transition-colors'
                >
                  View All Connectors
                </button>
              </div>
              <SftpConnectorManager
                companyId={selectedCompany.companyId}
                companyName={selectedCompany.companyName}
              />
            </div>
          ) : (
            <div>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-semibold text-slate-100'>All SFTP Connectors</h2>
                <button
                  onClick={() => setActiveTab('companies')}
                  className='px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors'
                >
                  Select Company
                </button>
              </div>
              <SftpConnectorManager />
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.company && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-slate-800 border border-white/10 rounded-lg p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-medium text-slate-100 mb-4'>Delete Company</h3>

            <div className='mb-4'>
              <p className='text-slate-300 mb-2'>
                Are you sure you want to delete <strong>{deleteModal.company.companyName}</strong>?
              </p>

              {deleteModal.company.userCount && deleteModal.company.userCount > 0 && (
                <div className='p-3 bg-red-900/20 border border-red-700/50 rounded text-red-300 text-sm'>
                  ⚠️ This company has {deleteModal.company.userCount} user(s). You must delete all
                  users first.
                </div>
              )}
            </div>

            <div className='mb-4'>
              <label className='flex items-center gap-2 text-sm text-slate-300'>
                <input
                  type='checkbox'
                  checked={deleteModal.removeFolders}
                  onChange={e =>
                    setDeleteModal(prev => ({ ...prev, removeFolders: e.target.checked }))
                  }
                  className='rounded border-white/10'
                />
                Remove S3 folders and files
              </label>
              <p className='text-xs text-slate-400 mt-1'>
                Check this to permanently delete all company data from S3
              </p>
            </div>

            <div className='flex gap-3'>
              <button
                onClick={closeDeleteModal}
                className='flex-1 px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  deleteModal.company &&
                  handleDeleteCompany(deleteModal.company, deleteModal.removeFolders)
                }
                disabled={loading || (deleteModal.company?.userCount || 0) > 0}
                className='flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors'
              >
                {loading ? 'Deleting...' : 'Delete Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      <SharedCreateCompanyModal
        isOpen={createModal.show}
        onClose={() => setCreateModal(prev => ({ ...prev, show: false }))}
        onCompanyCreated={async () => {
          setMessage('✅ Company created successfully');
          await loadCompanies();
        }}
        includeServiceAccount={true}
      />

      {/* Edit Company Modal */}
      <EditCompanyModal
        isOpen={editModal.show}
        onClose={closeEditModal}
        company={editModal.company}
        onSuccess={handleEditSuccess}
        onError={handleEditError}
        onReload={loadCompanies}
      />

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
